import React, { useEffect, useMemo, useState } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  IconButton,
  InputAdornment,
  TablePagination,
  Skeleton,
  useTheme,
  Switch,
} from '@mui/material'

import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined'
import ClearIcon from '@mui/icons-material/Clear'
import InputBase from '@mui/material/InputBase'
const debounce = <T extends (...args: any[]) => void>(fn: T, delay: number) => {
  let timer: ReturnType<typeof setTimeout>
  return (...args: Parameters<T>) => {
    clearTimeout(timer)
    timer = setTimeout(() => fn(...args), delay)
  }
}
import { Link, useNavigate } from 'react-router-dom'

const SharedTable = ({
  columns,
  data,
  page,
  rowsPerPage,
  totalRows,
  onPageChange,
  onRowsPerPageChange,
  tableBodyLoader,
  onSearch,
  searchFilter,
  searchFilterVisible,
  selectableRows,
  onRowClick,
  getRowHref,
  showDeletedToggle,
  switchProps,
  showPagination = true,
  initialSearchValue = '',
  // Opt-in escape hatch: skip the internal string filter and render `data`
  // exactly as given. Required whenever a cell is a React node — the filter
  // stringifies cells, so a node renders as "[object Object]" and a row the
  // caller already matched (e.g. by name) would be dropped here. Callers that
  // set this own filtering AND slicing, and pass the matching `totalRows`.
  disableClientFilter = false,
  // Opt-in sizing for a wide table that must fit without a sideways scroll:
  // `tableMinWidth` replaces the 150px-per-column floor, `dense` trims cell
  // padding. The defaults leave every other table as it was.
  tableMinWidth = undefined,
  dense = false,
  // Opt-in: clickable rows (selectableRows) also take focus and open on Enter/Space.
  rowKeyboardAccess = false,
  // Opt-in per-row gate for clickable tables: a row it rejects gets no click, cursor, hover or focus.
  isRowClickable = undefined,
  // Opt-in accessible name for a keyboard-focusable row, e.g. "Edit plan Pro".
  getRowAriaLabel = undefined,
}) => {
  const [searchValue, setSearchValue] = useState(initialSearchValue)
  const [inputValue, setInputValue] = useState(initialSearchValue)
  const [visibleData, setVisibleData] = useState([])
  const theme = useTheme()
  const navigate = useNavigate()
  const isDark = theme.palette.mode === 'dark'

  useEffect(() => {
    updateVisibleData()
  }, [data, page, rowsPerPage, searchValue])

  const handleChangePage = (event, newPage) => {
    onPageChange(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10)
    onRowsPerPageChange(newRowsPerPage)
  }

  const handleSearchChange = useMemo(
    () =>
      debounce((value) => {
        setSearchValue(value)
        onSearch && onSearch(value)
      }, 300),
    [onSearch]
  )

  const updateVisibleData = () => {
    const startIndex = page * rowsPerPage
    const endIndex = Math.min((page + 1) * rowsPerPage, totalRows)
    if (disableClientFilter) {
      setVisibleData(data)
      return
    }
    const filteredData = data?.filter((row) => {
      // Filter based on search value
      if (searchValue === '') {
        return true
      }
      for (const column of columns) {
        const cellValue = row[column.id]
        if (cellValue && cellValue.toString().toLowerCase().includes(searchValue.toLowerCase())) {
          return true
        }
      }
      return false
    })
    const slicedData = filteredData
    setVisibleData(slicedData)
  }

  const rowClickable = (row): boolean =>
    Boolean(selectableRows) &&
    (typeof isRowClickable !== 'function' || Boolean((isRowClickable as (r: unknown) => unknown)(row)))
  // Only rows the opt-in gate rejects lose the hover; every other table keeps it on all rows.
  const rowGatedOff = (row): boolean => typeof isRowClickable === 'function' && !rowClickable(row)

  const handleRowClick = (e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, row) => {
    if (!rowClickable(row)) return
    // A control inside the row (link, button, input...) handles its own click.
    const control = (e.target as HTMLElement).closest?.('a, button, input, select, textarea, [role="button"]')
    if (control && control !== e.currentTarget && e.currentTarget.contains(control)) return
    const href = getRowHref?.(row)
    if (href && (e.metaKey || e.ctrlKey)) {
      window.open(href, '_blank')
      return
    }
    onRowClick(row.id)
  }

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : '#e2e8f0'}`,
        borderRadius: '12px',
        overflow: 'auto',
        bgcolor: 'background.paper',
        width: '100%',
        maxWidth: '100%',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      {showPagination && (
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'stretch', sm: 'center' },
            flexDirection: { xs: 'column', sm: 'row' },
            flexWrap: { xs: 'nowrap', sm: 'wrap' },
            gap: 0,
            py: 1,
            borderBottom: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : '#e2e8f0'}`,
          }}
        >
          {searchFilterVisible && (
            <Box
              sx={{
                px: 2,
                py: 1,
                width: { xs: '100%', sm: 'auto' },
                boxSizing: 'border-box',
              }}
            >
              <InputBase
                placeholder="Search by name…"
                value={inputValue}
                sx={{
                  border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0'}`,
                  borderRadius: '10px',
                  width: { xs: '100%', sm: '280px' },
                  height: '36px',
                  px: 1,
                  fontSize: '13px',
                  fontWeight: 500,
                  color: 'text.primary',
                  bgcolor: isDark ? 'rgba(255, 255, 255, 0.03)' : '#f8fafc',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: isDark ? 'rgba(255, 255, 255, 0.2)' : '#94a3b8',
                    bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f1f5f9',
                  },
                  '&.Mui-focused': {
                    borderColor: '#2563eb',
                    boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.1)',
                    bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#fff',
                  },
                }}
                inputProps={{ 'aria-label': 'search' }}
                startAdornment={
                  <InputAdornment position="start">
                    <SearchOutlinedIcon sx={{ color: 'text.secondary', fontSize: 19, ml: 0.5 }} />
                  </InputAdornment>
                }
                endAdornment={
                  inputValue && (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="Clear search"
                        size="small"
                        onClick={() => {
                          setInputValue('')
                          handleSearchChange('')
                        }}
                        sx={{
                          p: 0.5,
                          '&:hover': {
                            bgcolor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)',
                          },
                        }}
                      >
                        <ClearIcon sx={{ color: 'text.secondary', fontSize: 16 }} />
                      </IconButton>
                    </InputAdornment>
                  )
                }
                onChange={(e) => {
                  setInputValue(e.target.value)
                  handleSearchChange(e.target.value)
                }}
              />
            </Box>
          )}
          {searchFilter}
          {showPagination && (tableBodyLoader || visibleData?.length > 0) && (
            <TablePagination
              rowsPerPageOptions={[10, 25, 100]}
              component="div"
              count={totalRows}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    sx: {
                      borderRadius: '8px',
                      border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0'}`,
                      boxShadow: isDark ? '0 8px 24px rgba(0, 0, 0, 0.6)' : '0 8px 24px rgba(0, 0, 0, 0.15)',
                      mt: 0.5,
                      '& .MuiMenuItem-root': {
                        fontSize: '13px',
                        fontWeight: 500,
                        borderRadius: '4px',
                        mx: 0.5,
                        '&:hover': {
                          bgcolor: isDark ? 'rgba(255, 255, 255, 0.06)' : '#f1f5f9',
                        },
                        '&.Mui-selected': {
                          bgcolor: isDark ? 'rgba(37, 99, 235, 0.15)' : 'rgba(37, 99, 235, 0.08)',
                          color: isDark ? '#60a5fa' : '#2563eb',
                          fontWeight: 600,
                          '&:hover': {
                            bgcolor: isDark ? 'rgba(37, 99, 235, 0.2)' : 'rgba(37, 99, 235, 0.12)',
                          },
                        },
                      },
                    },
                  },
                },
              }}
              sx={{
                flexGrow: { xs: 0, sm: 1 },
                width: { xs: '100%', sm: 'auto' },
                borderTop: { xs: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.06)' : '#e2e8f0'}`, sm: 'none' },
                '.MuiTablePagination-toolbar': {
                  flexWrap: 'wrap',
                  justifyContent: { xs: 'center', sm: 'flex-end' },
                  px: { xs: 1, sm: 2 },
                  gap: { xs: 0.5, sm: 0 },
                  minHeight: { xs: 'auto', sm: 52 },
                  py: { xs: 0.5, sm: 0 },
                },
                '.MuiTablePagination-selectLabel': {
                  display: { xs: 'none', sm: 'block' },
                },
                '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                  fontSize: { xs: '12px', sm: '13px' },
                  color: 'text.secondary',
                  fontWeight: 500,
                },
                '.MuiTablePagination-displayedRows': {
                  bgcolor: isDark ? 'rgba(255, 255, 255, 0.04)' : '#f1f5f9',
                  px: 1.5,
                  py: 0.5,
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: 600,
                  letterSpacing: '0.02em',
                },
                '.MuiTablePagination-select': {
                  border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.15)' : '#e2e8f0'}`,
                  borderRadius: '6px',
                  fontSize: '13px',
                  py: 0.5,
                  display: { xs: 'none', sm: 'flex' },
                },
                '.MuiTablePagination-actions button': {
                  borderRadius: '8px',
                  mx: 0.25,
                  '&:hover': {
                    bgcolor: isDark ? 'rgba(255, 255, 255, 0.06)' : '#f1f5f9',
                  },
                },
              }}
            />
          )}
          {showDeletedToggle && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1,
                ml: { xs: 0, sm: 'auto' },
                borderLeft: { xs: 'none', sm: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.06)' : '#e2e8f0'}` },
                borderTop: { xs: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.06)' : '#e2e8f0'}`, sm: 'none' },
              }}
            >
              <Box
                component="span"
                sx={{
                  fontWeight: 600,
                  color: 'text.secondary',
                  fontSize: '13px',
                  whiteSpace: 'nowrap',
                  userSelect: 'none',
                }}
              >
                {switchProps.label}
              </Box>
              <Switch
                color="primary"
                checked={switchProps.checked}
                onChange={switchProps.onChange}
                inputProps={{ 'aria-label': 'controlled' }}
              />
            </Box>
          )}
        </Box>
      )}

      <Table
        sx={{ minWidth: typeof tableMinWidth === 'number' ? tableMinWidth : Math.max(650, columns.length * 150) }}
        aria-label="data table"
      >
        {!tableBodyLoader && (
          <TableHead>
            <TableRow>
              {columns.map((column, index) => (
                <TableCell
                  key={index}
                  sx={{
                    height: '44px',
                    backgroundColor: isDark ? 'rgba(255, 255, 255, 0.03)' : '#f8fafc',
                    borderBottom: `2px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : '#e2e8f0'}`,
                    textTransform: 'none',
                    minHeight: '44px',
                    width: column.width,
                    wordBreak: 'break-word',
                    color: 'text.primary',
                    fontSize: { xs: '12px', sm: '13px' },
                    fontWeight: 600,
                    fontFamily: 'TomatoGroteskMedium',
                    letterSpacing: '0.02em',
                    py: dense ? 1 : { xs: 1, sm: 1.5 },
                    px: dense ? 1.25 : { xs: 1.5, sm: 2 },
                    whiteSpace: 'nowrap',
                  }}
                >
                  <Box
                    sx={
                      (column as any).align === 'center'
                        ? { display: 'flex', justifyContent: 'center' }
                        : (column as any).align === 'right' || (index === columns?.length - 1 && !(column as any).align)
                        ? { display: 'flex', justifyContent: 'flex-end' }
                        : {}
                    }
                  >
                    {column.label}
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
        )}
        <TableBody>
          {tableBodyLoader ? (
            <>
              {[...Array(5)].map((_, i) => (
                <TableRow key={i}>
                  {columns.map((_, j) => (
                    <TableCell key={j}>
                      <Skeleton variant="text" animation="wave" height={24} />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </>
          ) : visibleData?.length > 0 ? (
            visibleData.map((row, rowIndex) => (
              <TableRow
                key={rowIndex}
                onClick={(e) => handleRowClick(e, row)}
                {...(rowKeyboardAccess &&
                  rowClickable(row) && {
                    tabIndex: 0,
                    'aria-label':
                      typeof getRowAriaLabel === 'function'
                        ? String((getRowAriaLabel as (r: unknown) => unknown)(row))
                        : undefined,
                    onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => {
                      if (e.target !== e.currentTarget || (e.key !== 'Enter' && e.key !== ' ')) return
                      e.preventDefault()
                      handleRowClick(e, row)
                    },
                  })}
                sx={{
                  ...(rowKeyboardAccess &&
                    rowClickable(row) && {
                      '&:focus-visible': { outline: '2px solid #2563eb', outlineOffset: '-2px' },
                    }),
                  cursor: rowClickable(row) ? 'pointer' : 'default',
                  backgroundColor: row?.deleted ? (isDark ? 'rgba(239, 68, 68, 0.1)' : '#fef2f2') : 'inherit',
                  transition: 'background-color 0.15s ease',
                  ...(!rowGatedOff(row) && {
                    '&:hover': {
                      backgroundColor: row?.deleted
                        ? isDark
                          ? 'rgba(239, 68, 68, 0.15)'
                          : '#fee2e2'
                        : isDark
                        ? 'rgba(255, 255, 255, 0.03)'
                        : '#f8fafc',
                    },
                  }),
                  '&:last-child td': {
                    borderBottom: 0,
                  },
                }}
              >
                {columns.map((column, index) => (
                  <TableCell
                    key={index}
                    sx={{
                      whiteSpace: 'nowrap',
                      fontSize: { xs: '13px', sm: '14px' },
                      color: 'text.primary',
                      py: dense ? 1 : { xs: 1, sm: 1.5 },
                      px: dense ? 1.25 : { xs: 1.5, sm: 2 },
                      borderBottom: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0'}`,
                    }}
                  >
                    <Box
                      sx={
                        (column as any).align === 'center'
                          ? { display: 'flex', justifyContent: 'center' }
                          : (column as any).align === 'right' ||
                            (index === columns?.length - 1 && !(column as any).align)
                          ? { display: 'flex', justifyContent: 'flex-end' }
                          : {}
                      }
                    >
                      {row[column.id]}
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                align="center"
                colSpan={columns?.length || 6}
                sx={{
                  py: 6,
                  color: 'text.secondary',
                  fontSize: '15px',
                }}
              >
                No results found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export default SharedTable
